import { Box, Button, Drawer, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import React from 'react';
import { CommerceVariant, ProductOnUser } from 'common-ts/dist/models/Product';
import { formatRupiah, variantIsPurchasable } from './LinkPageShared';

// Bottom-sheet variant picker for buy-now on the /link page. A condensed
// version of the product page's option selector: option chips + price for
// the resolved variant + one CTA. (The full product-page selector is too
// entangled with the cart window flow to reuse here.)

const useStyles = makeStyles((theme: Theme) =>
createStyles({
  sheet: {
    borderRadius: '16px 16px 0px 0px',
    padding: '20px 20px 26px',
    maxWidth: '480px',
    margin: '0 auto',
  },
  title: {
    fontSize: '16px',
    fontWeight: 700,
    marginBottom: '4px',
  },
  optionName: {
    fontSize: '12px',
    fontWeight: 600,
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    margin: '14px 0px 8px',
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  chip: {
    padding: '7px 14px',
    border: '1px solid #c9c9c9',
    borderRadius: '18px',
    fontSize: '13px',
    cursor: 'pointer',
    userSelect: 'none',
  },
  chipSelected: {
    borderColor: '#1a1a1a',
    backgroundColor: '#1a1a1a',
    color: '#ffffff',
  },
  chipUnavailable: {
    opacity: 0.35,
    textDecoration: 'line-through',
  },
  priceRow: {
    margin: '16px 0px 4px',
  },
  marketplacePrice: {
    fontSize: '13px',
    color: '#8a8a8a',
    textDecoration: 'line-through',
  },
  directPrice: {
    fontSize: '19px',
    fontWeight: 700,
  },
  ctaButton: {
    marginTop: '14px',
    width: '100%',
    padding: '12px 0px',
    borderRadius: '10px',
    fontWeight: 700,
  },
}),
);

interface LinkPageVariantSheetProps {
  product: ProductOnUser | null;
  onClose: () => void;
  // Resolved variant chosen; parent runs the buy-now flow.
  onBuyNow: (product: ProductOnUser, variant: CommerceVariant) => void;
}

export default function LinkPageVariantSheet({ product, onClose, onBuyNow }: LinkPageVariantSheetProps) {
  const classes = useStyles();
  const [selected, setSelected] = React.useState<{ [optionName: string]: string }>({});

  // Reset selection whenever a different product opens the sheet.
  const productId = product?.productId;
  React.useEffect(() => { setSelected({}); }, [productId]);

  const commerce = product?.commerce;
  if (!product || !commerce) return null;

  const options = [...commerce.options].sort((a, b) => a.position - b.position);
  const allSelected = options.every((o) => selected[o.name]);
  const resolvedVariant: CommerceVariant | undefined = !options.length
    ? commerce.variants[0]
    : allSelected
      ? commerce.variants.find((v) => {
        const values = [v.option1, v.option2, v.option3];
        return options.every((o, i) => values[i] === selected[o.name]);
      })
      : undefined;

  // Same availability rule as the product page: a value is offered when some
  // purchasable variant matches it together with the other selected values.
  const valueIsAvailable = (optionIndex: number, value: string): boolean => {
    return commerce.variants.some((v) => {
      const values = [v.option1, v.option2, v.option3];
      if (values[optionIndex] !== value) return false;
      if (!variantIsPurchasable(v)) return false;
      return options.every((o, i) => {
        if (i === optionIndex) return true;
        const sel = selected[o.name];
        return !sel || values[i] === sel;
      });
    });
  };

  const outOfStock = resolvedVariant !== undefined && !variantIsPurchasable(resolvedVariant);
  const variantMarketplace = resolvedVariant?.marketplacePrice ?? null;
  const showComparison = resolvedVariant && variantMarketplace !== null
    && variantMarketplace !== undefined && variantMarketplace > resolvedVariant.price;

  return (
    <Drawer
      anchor="bottom"
      open={!!product}
      onClose={onClose}
      PaperProps={{ className: classes.sheet }}
    >
      <Typography className={classes.title}>{product.name}</Typography>
      {
        options.map((option, optionIndex) => (
          <Box key={option.name}>
            <Typography className={classes.optionName}>
              {option.name}{selected[option.name] ? ': ' + selected[option.name] : ''}
            </Typography>
            <Box className={classes.chipRow}>
              {
                option.values.map((value) => {
                  const isSelected = selected[option.name] === value;
                  const available = valueIsAvailable(optionIndex, value);
                  return (
                    <Box
                      key={value}
                      className={
                        classes.chip
                        + (isSelected ? ' ' + classes.chipSelected : '')
                        + (!available ? ' ' + classes.chipUnavailable : '')
                      }
                      onClick={() => setSelected((prev) => ({ ...prev, [option.name]: value }))}
                    >
                      {value}
                    </Box>
                  );
                })
              }
            </Box>
          </Box>
        ))
      }
      <Box className={classes.priceRow}>
        {
          resolvedVariant ?
          <>
            {showComparison &&
              <Typography className={classes.marketplacePrice}>
                Harga Marketplace {formatRupiah(variantMarketplace as number)}
              </Typography>
            }
            <Typography className={classes.directPrice}>
              {formatRupiah(resolvedVariant.price)}
            </Typography>
          </> :
          <Typography style={{ fontSize: '13px', color: '#8a8a8a' }}>
            Pilih varian dulu ya
          </Typography>
        }
      </Box>
      <Button
        className={classes.ctaButton}
        variant="contained"
        color="primary"
        disableElevation
        disabled={!resolvedVariant || outOfStock}
        onClick={() => {
          if (resolvedVariant) onBuyNow(product, resolvedVariant);
        }}
      >
        {outOfStock ? 'Stok Habis' : 'Beli Sekarang'}
      </Button>
    </Drawer>
  );
}
